import { useMemo, useState } from "react"
import { Wallet } from "lucide-react"
import { useGetBudgetItemsQuery, useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetItemGroupsQuery } from "shared/redux/endpoints/procurementItems"
import { FilterBar, DataTable, MoneyText, EmptyState, en } from "procurement/design"
import { formatVND } from "../../constants"

// `categoryRemaining` is the whole category × center figure for the month and
// repeats on every item row of that group — shown per row, never summed.
const itemColumns = () => [
  { key: "categoryName", header: "Category", sortable: true, render: (r) => r.categoryName || "—" },
  { key: "itemGroupName", header: "Item group", sortable: true, render: (r) => r.itemGroupName || "—" },
  {
    key: "itemName",
    header: "Item",
    strong: true,
    sortable: true,
    value: (r) => r.itemName || "",
    render: (r) =>
      r.itemId == null ? (
        <span title="Spend that isn't tied to a catalog item">Off-catalog</span>
      ) : (
        <span>
          {r.itemName}
          {r.itemCode && <span className="pc-hint"> · {r.itemCode}</span>}
        </span>
      ),
  },
  { key: "centerName", header: "Center", sortable: true, render: (r) => r.centerName || "—" },
  { key: "requested", header: "Requested", align: "right", sortable: true, render: (r) => <MoneyText value={r.requested} /> },
  { key: "committed", header: en.budget.committed, align: "right", sortable: true, render: (r) => <MoneyText value={r.committed} /> },
  { key: "approvedUnpaid", header: en.budget.approvedUnpaid, align: "right", sortable: true, render: (r) => <MoneyText value={r.approvedUnpaid} /> },
  { key: "approvedPaid", header: en.budget.approvedPaid, align: "right", sortable: true, render: (r) => <MoneyText value={r.approvedPaid} /> },
  {
    key: "overPaid",
    header: en.budget.overPaid,
    align: "right",
    sortable: true,
    render: (r) =>
      r.overPaid > 0 ? (
        <span style={{ color: "var(--pc-danger)" }}>
          <MoneyText value={r.overPaid} />
        </span>
      ) : (
        "—"
      ),
  },
  { key: "spentAndCommitted", header: "Spent + committed", align: "right", sortable: true, render: (r) => <MoneyText value={r.spentAndCommitted} /> },
  {
    key: "categoryRemaining",
    header: "Category remaining",
    align: "right",
    sortable: true,
    tooltip: "Whole category × center for the month — repeated on each item of that group.",
    render: (r) => (
      <div>
        <MoneyText value={r.categoryRemaining} />
        {r.isOverBudget && (
          <div style={{ color: "var(--pc-danger)", fontSize: 12 }}>{en.budget.overBy(formatVND(r.overSpentAmount))}</div>
        )}
      </div>
    ),
  },
]

const BudgetByItemTable = ({ fiscalYear, fiscalMonth, centerId, search }) => {
  const [extra, setExtra] = useState({ categoryId: "", itemGroupId: "" })

  const { data: categories = [] } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const { data: itemGroups = [] } = useGetItemGroupsQuery({ categoryId: extra.categoryId || undefined })

  const { data = [], isFetching, isError, refetch } = useGetBudgetItemsQuery({
    fiscalYear,
    fiscalMonth: fiscalMonth || undefined,
    centerId: centerId || undefined,
    categoryId: extra.categoryId || undefined,
    itemGroupId: extra.itemGroupId || undefined,
  })

  const rows = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    return data
      .filter(
        (r) =>
          !q ||
          [r.categoryName, r.itemGroupName, r.itemName, r.itemCode, r.centerName].some((v) =>
            (v || "").toLowerCase().includes(q)
          )
      )
      .map((r, i) => ({ ...r, key: `${r.categoryId}-${r.itemGroupId}-${r.itemId}-${r.centerId}-${r.fiscalMonth}-${i}` }))
  }, [data, search])

  return (
    <>
      <FilterBar
        values={extra}
        onChange={(patch) => setExtra((prev) => ({ ...prev, ...patch, ...(patch.categoryId !== undefined ? { itemGroupId: "" } : {}) }))}
        onReset={() => setExtra({ categoryId: "", itemGroupId: "" })}
        fields={[
          {
            key: "categoryId",
            label: "Category",
            type: "select",
            options: categories.map((c) => ({ value: String(c.id), label: c.name })),
            placeholder: "All categories",
          },
          {
            key: "itemGroupId",
            label: "Item group",
            type: "select",
            options: itemGroups.map((g) => ({ value: String(g.id), label: g.name || g.code })),
            placeholder: "All item groups",
          },
        ]}
      />
      {isError ? (
        <p className="pc-hint">
          Couldn&apos;t load the item breakdown.{" "}
          <button type="button" onClick={refetch} style={{ all: "unset", cursor: "pointer", color: "var(--brand-600)" }}>
            Retry
          </button>
        </p>
      ) : (
        <DataTable
          columns={itemColumns()}
          rows={rows}
          isLoading={isFetching}
          density="comfortable"
          empty={<EmptyState icon={<Wallet aria-hidden />} title={en.budget.emptyTitle} text={en.budget.emptyText} />}
        />
      )}
    </>
  )
}

export default BudgetByItemTable
